import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { LogOut, Lock } from 'lucide-react'
import { Avatar, MatchScore } from '@/lib/atsUi'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { candidates } from '@/lib/mocks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/portal/client')({
  component: ClientPortalPage,
})

// High-match candidates submitted to Acme Corp (j1 + j3), score >= 85
const SUBMITTED_JOB_IDS = ['j1', 'j3']
const portalCandidates = candidates
  .filter((c) => SUBMITTED_JOB_IDS.includes(c.jobId) && c.matchScore >= 85)
  .sort((a, b) => b.matchScore - a.matchScore)
  .slice(0, 4)

const JOB_TITLE: Record<string, string> = {
  j1: 'Senior Backend Engineer',
  j3: 'Data Scientist',
}

type CardStatus = 'pending' | 'shortlisted' | 'passed' | 'more_requested'

function whyMatched(skills: string[]): string {
  const top = skills.slice(0, 3).join(', ')
  return `Strong fit based on ${top} expertise`
}

function StatusPill({ status }: { status: CardStatus }) {
  if (status === 'pending') return null
  const map: Record<Exclude<CardStatus, 'pending'>, { label: string; className: string }> = {
    shortlisted: {
      label: 'Shortlisted',
      className: 'bg-success/15 text-success',
    },
    passed: {
      label: 'Passed',
      className: 'bg-muted text-muted-foreground',
    },
    more_requested: {
      label: 'More info requested',
      className: 'bg-warning/20 text-warning-foreground',
    },
  }
  const { label, className } = map[status]
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        className
      )}
    >
      {label}
    </span>
  )
}

function ClientPortalPage() {
  const [statuses, setStatuses] = useState<Record<string, CardStatus>>(() =>
    Object.fromEntries(portalCandidates.map((c) => [c.id, 'pending' as CardStatus]))
  )

  function handleAction(id: string, action: CardStatus, name: string) {
    setStatuses((prev) => ({ ...prev, [id]: action }))
    if (action === 'shortlisted') {
      toast.success(`${name} shortlisted — we'll coordinate next steps.`)
    } else if (action === 'passed') {
      toast("Feedback recorded. We'll keep looking.")
    } else if (action === 'more_requested') {
      toast("Request sent — we'll share additional details shortly.")
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-sm">Inumellas Consultancy Services</span>
          <span className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground">
            Client portal
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">
            Neha Kapoor · Acme Corp
          </span>
          <Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5" onClick={() => toast.success('Signed out.')}>
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">
            Candidates submitted for your review
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Shortlisted for your open roles. Approve to move forward, or send feedback.
          </p>
        </div>

        <div className="space-y-4">
          {portalCandidates.map((candidate) => {
            const status = statuses[candidate.id]
            const isDone = status !== 'pending'

            return (
              <Card key={candidate.id} className={cn(isDone && 'opacity-80')}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <Avatar name={candidate.name} className="mt-0.5 shrink-0" />

                    <div className="min-w-0 flex-1">
                      {/* Name row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm">{candidate.name}</span>
                        <MatchScore score={candidate.matchScore} />
                        {status !== 'pending' && <StatusPill status={status} />}
                      </div>

                      {/* Title + experience */}
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {candidate.title} · {candidate.totalYears} yrs experience
                      </p>

                      {/* Why matched */}
                      <p className="mt-2 text-sm text-foreground">
                        {whyMatched(candidate.skills)}
                      </p>

                      {/* Role tag */}
                      <p className="mt-1 text-xs text-muted-foreground">
                        Applied for:{' '}
                        <span className="font-medium text-foreground">
                          {JOB_TITLE[candidate.jobId] ?? candidate.appliedFor}
                        </span>
                      </p>

                      {/* Masked contact */}
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Lock className="h-3 w-3 shrink-0" />
                        Contact shared once you shortlist
                      </div>

                      {/* Action buttons */}
                      {!isDone && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAction(candidate.id, 'shortlisted', candidate.name)}
                          >
                            Shortlist
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAction(candidate.id, 'passed', candidate.name)}
                          >
                            Pass
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              handleAction(candidate.id, 'more_requested', candidate.name)
                            }
                          >
                            Request more info
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Questions? Reply to your dedicated recruiter at{' '}
          <a href="mailto:venkat@inumellas.com" className="underline underline-offset-2">
            venkat@inumellas.com
          </a>
        </p>
      </main>
    </div>
  )
}
